'use client'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React, { forwardRef, useCallback, useState } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Pressable } from '../pressable'
export type SwitchSize = 'sm' | 'md' | 'lg'

export type SwitchProps = {
    value?: boolean
    defaultValue?: boolean
    onValueChange?: (value: boolean) => void
    onToggle?: (value: boolean) => void
    isDisabled?: boolean
    disabled?: boolean
    size?: SwitchSize
    className?: string
    testID?: string
    accessibilityLabel?: string
}

const track = tva({
    base: 'h-[22px] w-[38px] justify-center rounded-full p-0.5 web:cursor-pointer transition-colors duration-200',
    variants: {
        on: {
            true: 'bg-action-default',
            false: 'bg-graphics-grey',
        },
        size: {
            sm: 'scale-75',
            md: '',
            lg: 'scale-125',
        },
        disabled: {
            true: 'opacity-40 web:cursor-not-allowed',
            false: '',
        },
    },
})

const thumb = tva({
    base: 'h-[18px] w-[18px] rounded-full bg-aos-text-on-primary transition-transform duration-200',
    variants: {
        on: {
            true: 'translate-x-[16px]',
            false: 'translate-x-0',
        },
    },
})

export const Switch = forwardRef<React.ComponentRef<typeof Pressable>, SwitchProps>(function Switch(
    {
        value,
        defaultValue = false,
        onValueChange,
        onToggle,
        isDisabled,
        disabled,
        size = 'md',
        className,
        testID,
        accessibilityLabel,
    },
    ref,
) {
    const isControlled = value !== undefined
    const [internalValue, setInternalValue] = useState(defaultValue)
    const on = (isControlled ? value : internalValue) ?? false
    const notInteractive = isDisabled ?? disabled ?? false

    const commit = useCallback(
        (next: boolean) => {
            if (notInteractive) {
                return
            }
            if (!isControlled) {
                setInternalValue(next)
            }
            onValueChange?.(next)
            onToggle?.(next)
        },
        [notInteractive, isControlled, onValueChange, onToggle],
    )

    return (
        <Pressable
            ref={ref}
            role='switch'
            accessibilityRole='switch'
            accessibilityState={{ checked: on, disabled: notInteractive }}
            aria-checked={on}
            accessibilityLabel={accessibilityLabel}
            disabled={notInteractive}
            onPress={() => commit(!on)}
            testID={testID}
            className={twMerge(track({ on, size, disabled: notInteractive }), className)}
        >
            <View className={thumb({ on })} />
        </Pressable>
    )
})

Switch.displayName = 'Switch'
